import React, { useState, useEffect, useCallback, useMemo } from "react";
import { CircularProgress, Alert, Card, Box, Grid, TablePagination, Chip } from "@mui/material";
import { Refresh, Download, LocalParking } from "@mui/icons-material";
import PropTypes from "prop-types";
import * as XLSX from "xlsx";

import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";
import DataTable from "shared/components/Tables/DataTable";

const colors = {
  primary: "#1e293b",
  secondary: "#0b4f6c",
  white: "#ffffff",
  success: "#27ae60",
  warning: "#ed6c02",
  info: "#2980b9",
};

// 📦 Datos de ejemplo para mostrar la tabla
const MOCK_RECORDS = [
  {
    id: 1,
    placa: "ABC-123",
    comercio: "Comercio Uno",
    ipComercio: "192.168.1.10",
    nombre: "Juan",
    apellido: "Pérez",
    telefono: "5551234567",
    createdAtFormatted: "01/01/2024 10:30:00",
  },
  {
    id: 2,
    placa: "XYZ-789",
    comercio: "Comercio Dos",
    ipComercio: "192.168.1.20",
    nombre: "María",
    apellido: "González",
    telefono: "5559876543",
    createdAtFormatted: "02/01/2024 11:45:00",
  },
  {
    id: 3,
    placa: "DEF-456",
    comercio: "Comercio Tres",
    ipComercio: "192.168.1.30",
    nombre: "Carlos",
    apellido: "López",
    telefono: "5554567890",
    createdAtFormatted: "03/01/2024 09:15:00",
  },
  {
    id: 4,
    placa: "GHI-789",
    comercio: "Comercio Cuatro",
    ipComercio: "192.168.1.40",
    nombre: "Ana",
    apellido: "Martínez",
    telefono: "5556543210",
    createdAtFormatted: "04/01/2024 14:20:00",
  },
  {
    id: 5,
    placa: "JKL-012",
    comercio: "Comercio Cinco",
    ipComercio: "192.168.1.50",
    nombre: "Pedro",
    apellido: "Ramírez",
    telefono: "5550123456",
    createdAtFormatted: "05/01/2024 16:00:00",
  },
];

/**
 * Componente para mostrar el estado de la placa con colores
 */
const PlateStatusChip = ({ placa }) => {
  if (!placa || placa === "N/A") {
    return <Chip label="Sin placa" size="small" variant="outlined" />;
  }

  return (
    <Chip
      label={placa}
      size="small"
      sx={{
        fontWeight: 600,
        backgroundColor: `${colors.secondary}15`,
        color: colors.secondary,
        border: `1px solid ${colors.secondary}30`,
        "&:hover": {
          backgroundColor: `${colors.secondary}25`,
        },
      }}
    />
  );
};

PlateStatusChip.propTypes = {
  placa: PropTypes.string,
};

PlateStatusChip.defaultProps = {
  placa: "",
};

function TableLazoContent({ refreshFlag, onError, lot = null }) {
  const [records, setRecords] = useState(MOCK_RECORDS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalRecords, setTotalRecords] = useState(MOCK_RECORDS.length);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  /**
   * Simula la carga de datos (solo para mantener la interfaz)
   */
  const fetchRecords = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Simular delay de red
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Filtrar por lote si existe (simulado)
      let filtered = [...MOCK_RECORDS];
      if (lot) {
        filtered = filtered.filter((r) => r.id === lot || r.id % lot === 0);
      }

      // Paginación simulada
      const offset = page * rowsPerPage;
      const paginated = filtered.slice(offset, offset + rowsPerPage);

      setRecords(paginated);
      setTotalRecords(filtered.length);
      if (onError) onError(null);
    } catch (err) {
      const errorMsg = err.message || "Error al cargar registros";
      setError(errorMsg);
      if (onError) onError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, lot, onError]);

  // Cargar datos iniciales y cuando cambia refreshFlag
  useEffect(() => {
    fetchRecords();
  }, [fetchRecords, refreshFlag]);

  // Cargar datos cuando cambia la paginación
  useEffect(() => {
    fetchRecords();
  }, [page, rowsPerPage]);

  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  /**
   * Exportar a Excel con todos los campos
   */
  const exportToExcel = useCallback(() => {
    if (records.length === 0) return;

    try {
      const exportData = records.map((item) => ({
        PLACA: item.placa || "",
        COMERCIO: item.comercio || "",
        "IP COMERCIO": item.ipComercio || "",
        NOMBRE: item.nombre || "",
        APELLIDO: item.apellido || "",
        TELEFONO: item.telefono || "",
        "FECHA CREACIÓN": item.createdAtFormatted || "",
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);

      ws["!cols"] = [
        { wch: 15 },
        { wch: 20 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 15 },
        { wch: 22 },
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Registros Vicidial");
      XLSX.writeFile(wb, `registros_vicidial_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (error) {
      console.error("Error al exportar:", error);
    }
  }, [records]);

  /**
   * Columnas de la tabla
   */
  const columns = useMemo(
    () => [
      {
        Header: "ID",
        accessor: "id",
        width: "5%",
      },
      {
        Header: "Placa",
        accessor: "placa",
        width: "12%",
        // eslint-disable-next-line react/prop-types
        Cell: ({ value }) => <PlateStatusChip placa={value} />,
      },
      {
        Header: "Comercio",
        accessor: "comercio",
        width: "15%",
      },
      {
        Header: "IP Comercio",
        accessor: "ipComercio",
        width: "13%",
      },
      {
        Header: "Nombre",
        accessor: "nombre",
        width: "15%",
      },
      {
        Header: "Apellido",
        accessor: "apellido",
        width: "15%",
      },
      {
        Header: "Teléfono",
        accessor: "telefono",
        width: "13%",
      },
      {
        Header: "Fecha Creación",
        accessor: "createdAt",
        width: "12%",
      },
    ],
    []
  );

  /**
   * Filas de la tabla
   */
  const rows = useMemo(
    () =>
      records.map((item) => ({
        id: item.id,
        placa: item.placa || "N/A",
        comercio: item.comercio || "N/A",
        ipComercio: item.ipComercio || "N/A",
        nombre: item.nombre || "N/A",
        apellido: item.apellido || "N/A",
        telefono: item.telefono || "N/A",
        createdAt: item.createdAtFormatted || "N/A",
        raw: item,
      })),
    [records]
  );

  return (
    <MDBox>
      <Card
        sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 8px 24px rgba(0,0,0,0.12)" }}
      >
        <MDBox
          p={3}
          sx={{
            background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
          }}
        >
          <Grid container alignItems="center" justifyContent="space-between" spacing={2}>
            <Grid item xs={12} md={6}>
              <MDTypography variant="h4" color="white" fontWeight="bold">
                Registros Vicidial
              </MDTypography>
              <MDTypography variant="body2" color="white" opacity={0.8}>
                {lot ? `Filtro: ${lot} - ` : ""}Total: {totalRecords.toLocaleString()} registros
              </MDTypography>
            </Grid>
            <Grid item xs={12} md={6}>
              <MDBox display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
                <MDButton
                  variant="contained"
                  onClick={exportToExcel}
                  startIcon={<Download />}
                  disabled={records.length === 0}
                  sx={{
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 600,
                    bgcolor: colors.white,
                    color: colors.primary,
                    "&:hover": { bgcolor: "rgba(255,255,255,0.95)" },
                    "&:disabled": {
                      bgcolor: "rgba(255,255,255,0.3)",
                      color: "rgba(255,255,255,0.5)",
                    },
                  }}
                >
                  Exportar Excel
                </MDButton>
                <MDButton
                  variant="contained"
                  onClick={fetchRecords}
                  disabled={loading}
                  startIcon={<Refresh />}
                  sx={{
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 600,
                    bgcolor: colors.white,
                    color: colors.primary,
                    "&:hover": { bgcolor: "rgba(255,255,255,0.95)" },
                  }}
                >
                  {loading ? "Cargando..." : "Actualizar"}
                </MDButton>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>

        {loading ? (
          <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress sx={{ color: colors.secondary }} />
          </MDBox>
        ) : error ? (
          <Alert severity="error" sx={{ m: 3 }}>
            <MDTypography variant="body2" fontWeight="bold">
              Error al cargar los datos
            </MDTypography>
            <MDTypography variant="caption">{error}</MDTypography>
          </Alert>
        ) : records.length === 0 ? (
          <MDBox p={5} textAlign="center">
            <LocalParking sx={{ fontSize: 64, color: "#cbd5e1", mb: 2 }} />
            <MDTypography variant="h6" sx={{ color: "#64748b" }}>
              No hay registros disponibles
            </MDTypography>
            <MDTypography variant="body2" sx={{ color: "#94a3b8", mt: 1 }}>
              Importe un archivo Excel para comenzar
            </MDTypography>
          </MDBox>
        ) : (
          <>
            <DataTable
              table={{ columns, rows }}
              isSorted={true}
              entriesPerPage={rowsPerPage}
              showTotalEntries={false}
              noEndBorder
              canSearch={false}
              disablePagination={true}
            />

            <Card sx={{ mt: 2, borderRadius: "12px", overflow: "hidden" }}>
              <TablePagination
                component="div"
                count={totalRecords}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} de ${count.toLocaleString()}`
                }
                sx={{
                  borderBottom: "none",
                  "& .MuiTablePagination-toolbar": { minHeight: "56px" },
                }}
              />
            </Card>
          </>
        )}
      </Card>
    </MDBox>
  );
}

TableLazoContent.propTypes = {
  refreshFlag: PropTypes.bool,
  onError: PropTypes.func,
  lot: PropTypes.number,
};

TableLazoContent.defaultProps = {
  refreshFlag: false,
  onError: null,
  lot: null,
};

export default TableLazoContent;
